import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import {
  Copy,
  Check,
  Download,
  Share2,
  Link as LinkIcon,
  Tv,
  ShieldCheck,
  History,
} from "lucide-react";

function urls(slug, origin) {
  return [
    { key: "booking", label: "Booking", url: `${origin}/s/${slug}/book`, Icon: LinkIcon },
    { key: "tv", label: "TV Display", url: `${origin}/s/${slug}/tv`, Icon: Tv },
    { key: "admin", label: "Admin", url: `${origin}/s/${slug}/admin`, Icon: ShieldCheck },
    { key: "history", label: "History", url: `${origin}/s/${slug}/history`, Icon: History },
  ];
}

export default function ShopLinksPanel({ shop }) {
  const canvasRef = useRef(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const bookingUrl = `${origin}/s/${shop.slug}/book`;
  const list = urls(shop.slug, origin);

  useEffect(() => {
    if (!canvasRef.current) return;
    const accent = shop.brand_color || "#E6C662";
    QRCode.toCanvas(canvasRef.current, bookingUrl, {
      width: 220,
      margin: 1,
      color: { dark: accent, light: "#0A0A0A" },
      errorCorrectionLevel: "H",
    }).catch((err) => console.warn("QR render failed", err));
  }, [bookingUrl, shop.brand_color]);

  async function copyOne(key, url) {
    await navigator.clipboard.writeText(url);
    setCopiedKey(key);
    toast.success(`${key} link copied`);
    setTimeout(() => setCopiedKey(null), 1500);
  }

  async function copyAll() {
    const text = list.map((l) => `${l.label}: ${l.url}`).join("\n");
    await navigator.clipboard.writeText(text);
    toast.success("All 4 links copied to clipboard");
  }

  function shareWhatsApp() {
    const msg = `Welcome to ${shop.name}! Join our queue here: ${bookingUrl}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  }

  async function downloadPoster() {
    const canvas = document.createElement("canvas");
    const W = 1080;
    const H = 1620;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    const accent = shop.brand_color || "#E6C662";

    // Background
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, W, H);

    // Gold border
    ctx.strokeStyle = accent;
    ctx.lineWidth = 4;
    ctx.strokeRect(40, 40, W - 80, H - 80);
    ctx.strokeStyle = "rgba(230,198,98,0.25)";
    ctx.lineWidth = 1;
    ctx.strokeRect(70, 70, W - 140, H - 140);

    // Brand strip top
    ctx.fillStyle = accent;
    ctx.font = "600 28px 'IBM Plex Mono', monospace";
    ctx.textAlign = "center";
    ctx.fillText("AIBOX  ·  SMART  QUEUE  ·  PREMIUM", W / 2, 150);

    // Shop name
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "italic 96px 'Playfair Display', serif";
    ctx.textAlign = "center";
    ctx.fillText(shop.name, W / 2, 300);

    // Tagline
    if (shop.tagline) {
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.font = "400 30px 'Outfit', sans-serif";
      ctx.fillText(shop.tagline, W / 2, 360);
    }

    // Call to action
    ctx.fillStyle = accent;
    ctx.font = "600 36px 'Outfit', sans-serif";
    ctx.fillText("SCAN  TO  BOOK  YOUR  TOKEN", W / 2, 470);

    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.font = "400 22px 'Outfit', sans-serif";
    ctx.fillText("Skip the wait. Reserve your spot in seconds.", W / 2, 510);

    // Generate large QR
    const dataUrl = await QRCode.toDataURL(bookingUrl, {
      width: 640,
      margin: 1,
      color: { dark: "#FFFFFF", light: "#050505" },
      errorCorrectionLevel: "H",
    });
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = dataUrl;
    });

    // QR card
    const qrSize = 640;
    const qrX = (W - qrSize) / 2;
    const qrY = 580;
    // Card background
    ctx.fillStyle = "#0A0A0A";
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    const pad = 30;
    roundRect(ctx, qrX - pad, qrY - pad, qrSize + pad * 2, qrSize + pad * 2, 24);
    ctx.fill();
    ctx.stroke();
    ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

    // URL caption
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "500 26px 'IBM Plex Mono', monospace";
    ctx.fillText(bookingUrl.replace(/^https?:\/\//, ""), W / 2, qrY + qrSize + 90);

    // Bottom strip
    ctx.fillStyle = "rgba(230,198,98,0.7)";
    ctx.font = "400 22px 'IBM Plex Mono', monospace";
    ctx.fillText("POWERED  BY  AIBOX  GLOBAL  ·  AIBOXHUB.IN", W / 2, H - 110);

    // Download
    canvas.toBlob((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `aibox-${shop.slug}-poster.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }, "image/png");
    toast.success("Poster downloaded");
  }

  return (
    <div data-testid="shop-links-panel">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Links list */}
        <div className="lg:col-span-7 rounded-2xl bg-charcoal border border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40">
              Shareable Links
            </div>
            <button
              onClick={copyAll}
              data-testid="copy-all-links"
              className="btn-ghost text-xs inline-flex items-center gap-1.5 py-1.5 px-3"
            >
              <Copy className="w-3.5 h-3.5" /> Copy All
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {list.map(({ key, label, url, Icon }) => (
              <div
                key={key}
                className="flex items-center gap-3 rounded-xl border border-white/10 px-4 py-3 hover:border-gold/30 transition-colors"
              >
                <Icon className="w-4 h-4 text-gold flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-numbers tracking-[0.25em] uppercase text-white/40">
                    {label}
                  </div>
                  <div className="font-numbers text-xs text-white/70 truncate" title={url}>
                    {url}
                  </div>
                </div>
                <button
                  onClick={() => copyOne(key, url)}
                  data-testid={`copy-${key}`}
                  className="text-white/50 hover:text-gold p-2"
                >
                  {copiedKey === key ? (
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={shareWhatsApp}
              data-testid="share-whatsapp"
              className="btn-gold text-sm py-2 px-4 inline-flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" /> Share via WhatsApp
            </button>
            <button
              onClick={downloadPoster}
              data-testid="download-qr-poster"
              className="btn-ghost text-sm py-2 px-4 inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Download QR Poster
            </button>
          </div>
        </div>

        {/* Preview QR */}
        <div className="lg:col-span-5 rounded-2xl bg-charcoal border border-white/10 p-6 flex flex-col items-center justify-center">
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 mb-4">
            Booking QR · Live
          </div>
          <div className="rounded-2xl bg-void border border-gold/30 p-5 gold-glow">
            <canvas ref={canvasRef} data-testid="booking-qr-canvas" />
          </div>
          <div className="mt-4 text-xs font-numbers text-white/40 text-center break-all max-w-full px-2">
            {bookingUrl.replace(/^https?:\/\//, "")}
          </div>
        </div>
      </div>
    </div>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
